import React from 'react';
import {Button, Card, CardContent, CardActions, Grid, Typography, Chip, Box} from "@mui/material";
import {makeStyles} from "@mui/styles";
import {theme} from "../../theme/theme";
import {Property} from "../../redux/residentPortalApi";
import {developmentInfo, formatPropertyDesc, formatPropertyNumber} from "../../utils/propertyUtils";
import HomeIcon from '@mui/icons-material/Home';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {useRouter} from "../../hooks/useRouter";

interface OwnProps {
  property: Property;
}

const useStyles = makeStyles({
  root: {
    padding: theme.spacing(3),
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '12px',
    margin: theme.spacing(2),
    boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    overflow: 'hidden',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0 12px 20px rgba(0,0,0,0.15)',
    },
  },
  cardMedia: {
    position: 'relative',
    height: '140px',
    backgroundColor: '#f5f5f5',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  cardOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.4))',
    zIndex: 1,
  },
  cardContent: {
    flexGrow: 1,
    padding: theme.spacing(4),
    position: 'relative',
  },
  cardActions: {
    padding: theme.spacing(2, 4),
    borderTop: '1px solid #f0f0f0',
  },
  cardTitle: {
    fontWeight: 600,
    color: '#2c3e50',
    marginBottom: theme.spacing(1),
  },
  cardSubtitle: {
    color: '#34495e',
    fontSize: '18px',
    fontWeight: 500,
    marginBottom: theme.spacing(2),
  },
  cardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    zIndex: 0,
  },
  propertyChip: {
    margin: theme.spacing(0.5),
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    fontWeight: 500,
  },
  iconText: {
    display: 'flex',
    alignItems: 'center',
    color: '#7f8c8d',
    marginTop: theme.spacing(1),
    '& svg': {
      marginRight: theme.spacing(1),
      fontSize: '1rem',
    },
  },
  viewButton: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: '8px',
    padding: theme.spacing(1, 3),
    backgroundColor: '#3498db',
    color: 'white',
    '&:hover': {
      backgroundColor: '#2980b9',
    },
  },
});

const PropertyCard: React.FC<OwnProps> = (props) => {
  const {property} = props;
  const router = useRouter();
  const classes = useStyles();
  const devInfo = property && developmentInfo(property.development, property.phase, property.type);

  return (
    <Grid item xs={12} sm={6} md={4}>
      <Card elevation={0} className={classes.card}>
        <div className={classes.cardMedia}>
          <div className={classes.cardOverlay} />
          <img 
            src={devInfo.banner} 
            alt={devInfo.name} 
            className={classes.cardImage} 
          />
        </div>
        <CardContent className={classes.cardContent}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Typography variant="h4" className={classes.cardTitle}>
              {devInfo.name}
            </Typography>
            <Chip 
              label={property.type ? property.type.toUpperCase() : 'PROPERTY'} 
              size="small" 
              className={classes.propertyChip} 
            />
          </Box>
          
          <Typography variant="h5" className={classes.cardSubtitle}>
            {formatPropertyNumber(property)}
          </Typography>
          
          <Box mt={2}>
            <Typography variant="body2" className={classes.iconText} sx={{textTransform: 'capitalize'}}>
              <HomeIcon /> {property.type}
            </Typography>
            <Typography variant="body2" className={classes.iconText}>
              <LocationOnIcon /> {property.phase && formatPropertyDesc(property)}
            </Typography>
          </Box>
        </CardContent>
        
        <CardActions className={classes.cardActions}>
          <Button 
            onClick={() => router.push(`/property/${property.id}`)}
            variant="contained" 
            className={classes.viewButton}
            endIcon={<ArrowForwardIcon />}
            fullWidth
          >
            View Details
          </Button>
        </CardActions>
      </Card>
    </Grid>
  );
};

export default PropertyCard;